import { useMemo } from "react";
import Axios from "axios";
import { useQueries, useQuery } from "react-query";
import { getFloorAvailability } from "../../../utils/axios-requests";
import { bookingFavoritesQueryKeys } from "../../../hooks/Remote/BookingFavorites/booking-favorites.query-keys";
import type {
  BookingFavoriteDto,
  BookingFavoritesListResponse
} from "../../../hooks/Remote/BookingFavorites/booking-favorites.types";
import { useRemoteFetchFloorPlan } from "../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlan";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import {
  buildColleagueFloorAvailabilityArgs,
  colleagueFavoriteFloorLookup,
  colleagueFloorAnchorId,
  colleagueFloorPlanIsoRange,
  toColleagueFloorChips
} from "./colleague-workplace-floor.functions";

type Args = {
  enabled: boolean;
  locationInventoryId?: number;
  date: string;
  startTime: string;
  endTime: string;
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  selectedFloorInventoryId?: number;
  highlightInventoryId?: number;
  availabilityBookingType?: BookingType;
  preferredFloorId?: number;
  preferredFloorName?: string;
};

export function useColleagueWorkplaceFloorData({
  enabled,
  locationInventoryId,
  date,
  startTime,
  endTime,
  user,
  selectedFloorInventoryId,
  highlightInventoryId,
  availabilityBookingType,
  preferredFloorId
}: Args) {
  const availabilityArgs = useMemo(() => {
    if (locationInventoryId == null || locationInventoryId <= 0 || date === "") {
      return null;
    }

    return buildColleagueFloorAvailabilityArgs({
      locationInventoryId,
      user,
      date,
      startTime,
      endTime,
      bookingType: availabilityBookingType
    });
  }, [availabilityBookingType, date, endTime, locationInventoryId, startTime, user]);

  const availabilityQuery = useQuery(
    ["colleagueWorkplaceFloorAvailability", availabilityArgs],
    async () => {
      if (!availabilityArgs) {
        return [];
      }
      const { data } = await getFloorAvailability(availabilityArgs);
      return data as FloorAvailability[];
    },
    { enabled: enabled && availabilityArgs != null }
  );

  const floorInventory = availabilityQuery.data ?? [];
  const floorChips = useMemo(() => toColleagueFloorChips(floorInventory), [floorInventory]);
  const anchorFloorId = useMemo(
    () => colleagueFloorAnchorId(floorInventory, highlightInventoryId, preferredFloorId),
    [floorInventory, highlightInventoryId, preferredFloorId]
  );
  const seekFavoriteFloor =
    enabled &&
    (selectedFloorInventoryId == null || selectedFloorInventoryId <= 0) &&
    anchorFloorId == null &&
    floorInventory.length > 0;
  const favoriteBookingType = availabilityBookingType ?? BookingType.WORKPLACE;
  const favoriteFloor = useColleagueFavoriteFloorId(
    floorInventory,
    seekFavoriteFloor,
    favoriteBookingType
  );

  const resolvedFloorId = useMemo(() => {
    if (selectedFloorInventoryId != null && selectedFloorInventoryId > 0) {
      return selectedFloorInventoryId;
    }
    if (anchorFloorId != null) {
      return anchorFloorId;
    }
    if (favoriteFloor.floorId != null) {
      return favoriteFloor.floorId;
    }
    if (favoriteFloor.isPending) {
      return undefined;
    }
    return floorInventory[0]?.id;
  }, [
    anchorFloorId,
    favoriteFloor.floorId,
    favoriteFloor.isPending,
    floorInventory,
    selectedFloorInventoryId
  ]);

  const currentFloor = useMemo(() => {
    if (!floorInventory.length || resolvedFloorId == null) {
      return undefined;
    }
    return floorInventory.find(floor => floor.id === resolvedFloorId) ?? floorInventory[0];
  }, [floorInventory, resolvedFloorId]);

  const planRange = useMemo(
    () => colleagueFloorPlanIsoRange(date, startTime, endTime),
    [date, endTime, startTime]
  );

  const floorPlanId = currentFloor?.id ?? 0;
  const floorPlanQuery = useRemoteFetchFloorPlan(
    floorPlanId,
    planRange?.start ?? "",
    planRange?.end ?? null,
    enabled && floorPlanId > 0 && planRange != null
  );

  return {
    floorInventory,
    floorChips,
    currentFloor,
    resolvedFloorId: currentFloor?.id,
    floorPlan: floorPlanQuery.data,
    isLoading: availabilityQuery.isLoading || floorPlanQuery.isLoading || favoriteFloor.isPending,
    isError: availabilityQuery.isError || floorPlanQuery.isError,
    refetch: () => {
      availabilityQuery.refetch();
      floorPlanQuery.refetch();
      favoriteFloor.refetch();
    }
  };
}

function useColleagueFavoriteFloorId(
  floors: FloorAvailability[],
  enabled: boolean,
  bookingType: BookingType
) {
  const queries = useQueries(
    floors.map(floor => ({
      queryKey: bookingFavoritesQueryKeys.byFloor(floor.id),
      queryFn: async () => {
        const { data } = await Axios.get<BookingFavoritesListResponse>(
          "/booking-favorites/by-floor",
          { params: { floorInventoryId: floor.id } }
        );
        return data;
      },
      enabled,
      retry: false
    }))
  );

  const lookup = useMemo(() => {
    if (!enabled) {
      return { floorId: undefined, isPending: false };
    }

    const lists = floors.map((_, index) => {
      const query = queries[index];
      if (query == null) {
        return undefined;
      }
      return favoritesOfType(favoriteListForQuery(query), bookingType);
    });
    return colleagueFavoriteFloorLookup(floors, lists);
  }, [bookingType, enabled, floors, queries]);

  return {
    floorId: lookup.floorId,
    isPending: enabled && lookup.isPending,
    refetch: () => {
      queries.forEach(query => {
        query.refetch();
      });
    }
  };
}

function favoritesOfType(
  favorites: readonly BookingFavoriteDto[] | undefined,
  bookingType: BookingType
): readonly BookingFavoriteDto[] | undefined {
  if (favorites == null) {
    return undefined;
  }

  return favorites.filter(favorite => favorite.bookingType === bookingType);
}

function favoriteListForQuery(query: {
  status: string;
  data?: BookingFavoritesListResponse;
}): readonly BookingFavoriteDto[] | undefined {
  if (query.status === "loading" || query.status === "idle") {
    return undefined;
  }
  if (query.status === "error") {
    return [];
  }
  return query.data?.favorites ?? [];
}
